// src/pages/ProfilePage.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { updateProfile, updatePassword } from "firebase/auth";
import {
  doc,
  updateDoc,
  getDoc,
  serverTimestamp,
  Timestamp,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  orderBy,
  setDoc,
  deleteField,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage, auth } from "../firebase";
import styles from "../styles/ProfilePage.module.css";

const DEFAULT_AVATAR = "/default-avatar.png";

/* =========================
   Helpers (TZ + Dates)
   ========================= */
const TZ = "Asia/Jerusalem";

const formatDateForInput = (dateValue) => {
  if (!dateValue) return "";
  try {
    let date;
    if (dateValue instanceof Timestamp) date = dateValue.toDate();
    else if (typeof dateValue === "string") date = new Date(dateValue + "T00:00:00");
    else if (dateValue instanceof Date) date = dateValue;
    else return "";
    if (isNaN(date.getTime())) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  } catch (err) {
    console.error("Error formatting date:", err);
    return "";
  }
};

const nowMonthInTZ = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
  }).formatToParts(new Date());
  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${map.year}-${map.month}`;
};

const listMonthDatesTZ = (yyyy, mm) => {
  const dates = [];
  const startUTC = Date.UTC(yyyy, mm - 1, 1, 12, 0, 0);
  const endUTC = Date.UTC(yyyy, mm, 1, 12, 0, 0);
  for (let t = startUTC; t < endUTC; t += 24 * 60 * 60 * 1000) {
    const d = new Date(t);
    const p = new Intl.DateTimeFormat("en-CA", {
      timeZone: TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
    dates.push(p);
  }
  return dates;
};

const fmtHHMM = (ms) => {
  if (!ms || !isFinite(ms) || ms <= 0) return "0:00";
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
};

// Per-employee work schedule (mirrors WorkHoursManagementPage). workingDays holds weekday indices
// (0=Sunday … 6=Saturday); default is everything except Friday.
const WEEKDAY_NAME_TO_INDEX = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
const DEFAULT_WORKING_DAYS = [0, 1, 2, 3, 4, 6];
const weekdayIndexForDateStr = (ds) => {
  const [y, m, d] = ds.split("-").map(Number);
  const atNoonUTC = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  const wd = new Intl.DateTimeFormat("en-US", { timeZone: TZ, weekday: "long" }).format(atNoonUTC);
  return WEEKDAY_NAME_TO_INDEX[wd];
};
const getUserSchedule = (data) => {
  const ws = (data && data.workSchedule) || {};
  const workingDays = Array.isArray(ws.workingDays) && ws.workingDays.length ? ws.workingDays : DEFAULT_WORKING_DAYS;
  const requiredHoursPerDay = Number(ws.requiredHoursPerDay) || 0;
  return { workingDays, requiredHoursPerDay };
};

/* ===== DST-safe local->UTC conversion in TZ ===== */
const getTzOffsetMs = (timeZone, instant) => {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = dtf.formatToParts(instant);
  const map = {};
  for (const { type, value } of parts) map[type] = value;
  const asUTC = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second)
  );
  return asUTC - instant.getTime();
};

const makeUtcFromLocalInTZ = (y, m, d, hh, mm, timeZone = TZ) => {
  const guess = new Date(Date.UTC(y, m - 1, d, hh, mm, 0));
  const offsetMs = getTzOffsetMs(timeZone, guess);
  return new Date(Date.UTC(y, m - 1, d, hh, mm, 0) - offsetMs);
};

const makeAttendanceId = (userId, dateStr) => `${userId}_${dateStr}`;

export default function ProfilePage() {
  const { currentUser, userRole, loading: authLoading } = useAuth();

  // Profile state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [initialDateOfBirth, setInitialDateOfBirth] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [initialWhatsappNumber, setInitialWhatsappNumber] = useState("");
  const [roleDisplay, setRoleDisplay] = useState("");
  const [currentPhotoURL, setCurrentPhotoURL] = useState(DEFAULT_AVATAR);
  const [newAvatarFile, setNewAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(DEFAULT_AVATAR);

  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileSuccess, setProfileSuccess] = useState("");

  // Password
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  // Attendance summary
  const [month, setMonth] = useState(nowMonthInTZ());
  const [attLoading, setAttLoading] = useState(false);
  const [attError, setAttError] = useState("");
  const [attSummary, setAttSummary] = useState({
    totalWorkMs: 0,
    extraMs: 0,
    requiredMs: 0,
    remainingMs: 0,
    absences: 0,
    vacationDays: 0,
    paidVacationDays: 0,
    unpaidVacationDays: 0,
    presentDays: 0,
    completeDays: 0,
    workingDays: 0,
  });

  // Leave request (now sends to workHoursRequests)
  const [lrStart, setLrStart] = useState("");
  const [lrEnd, setLrEnd] = useState("");
  const [lrType, setLrType] = useState("Paid");
  const [lrReason, setLrReason] = useState("");
  const [lrBusy, setLrBusy] = useState(false);
  const [lrMsg, setLrMsg] = useState("");

  // Requests list (now reads from workHoursRequests)
  const [myRequests, setMyRequests] = useState([]);
  const [reqLoading, setReqLoading] = useState(false);

  // Add/Edit Hours (now sends request to manager)
  const [hoursDate, setHoursDate] = useState("");
  const [hoursIn, setHoursIn] = useState("");
  const [hoursOut, setHoursOut] = useState("");
  const [hoursBusy, setHoursBusy] = useState(false);
  const [hoursMsg, setHoursMsg] = useState("");

  // ✅ NEW: details required for hours request
  const [hoursDetails, setHoursDetails] = useState("");

  // Extra (compensatory) hours request — hours worked outside normal check-in/out (e.g. made up
  // late time from home). Manager approves and the hours get added to the monthly total.
  const [xhDate, setXhDate] = useState("");
  const [xhHours, setXhHours] = useState("");
  const [xhFrom, setXhFrom] = useState("Home");
  const [xhDetails, setXhDetails] = useState("");
  const [xhBusy, setXhBusy] = useState(false);
  const [xhMsg, setXhMsg] = useState("");

  // Sections open/close
  const [open, setOpen] = useState({
    hours: false,
    extra: false,
    profile: true,
    leave: false,
    password: false,
  });

  const toggle = (key) => setOpen((p) => ({ ...p, [key]: !p[key] }));

  // Per-section "?" help popovers
  const [helpOpen, setHelpOpen] = useState({});
  const toggleHelp = (key) => setHelpOpen((p) => ({ ...p, [key]: !p[key] }));

  const fileInputRef = useRef(null);

  const [yyyy, mm] = useMemo(() => month.split("-").map(Number), [month]);
  const allMonthDates = useMemo(() => listMonthDatesTZ(yyyy, mm), [yyyy, mm]);

  // Populate profile
  useEffect(() => {
    if (!authLoading && currentUser) {
      const currentName = currentUser.displayName || "";
      const currentEmail = currentUser.email || "";
      const photo = currentUser.photoURL || DEFAULT_AVATAR;

      setName(currentName);
      setEmail(currentEmail);
      setCurrentPhotoURL(photo);
      setAvatarPreview(photo);
      setRoleDisplay(userRole || "Loading role...");

      (async () => {
        try {
          const userDocRef = doc(db, "users", currentUser.uid);
          const docSnap = await getDoc(userDocRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (!userRole) setRoleDisplay(data.role || "N/A");
            const formattedDob = formatDateForInput(data.dateOfBirth);
            setDateOfBirth(formattedDob);
            setInitialDateOfBirth(formattedDob);
            const wa = data.whatsappNumber || "";
            setWhatsappNumber(wa);
            setInitialWhatsappNumber(wa);
          } else {
            if (!userRole) setRoleDisplay("Role not found");
          }
        } catch (err) {
          console.error(err);
          if (!userRole) setRoleDisplay("Error loading role");
          setProfileError("Error loading details.");
        }
      })();
    } else if (!authLoading && !currentUser) {
      setProfileError("User data not available. Please log in again.");
    }
  }, [currentUser, userRole, authLoading]);

  // Default hoursDate
  useEffect(() => {
    if (!hoursDate && allMonthDates.length) setHoursDate(allMonthDates[0]);
    if (hoursDate && !hoursDate.startsWith(month)) setHoursDate(allMonthDates[0] || "");
  }, [month, allMonthDates, hoursDate]);

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      if (file.size > 5 * 1024 * 1024) {
        setProfileError("File too large (Max 5MB).");
        setNewAvatarFile(null);
        setAvatarPreview(currentPhotoURL);
        return;
      }
      setNewAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setAvatarPreview(reader.result);
      reader.readAsDataURL(file);
      setProfileError("");
    } else {
      setProfileError("Please select a valid image file.");
      setNewAvatarFile(null);
      setAvatarPreview(currentPhotoURL);
    }
  };

  const hasProfileChanged = () => {
    const nameChanged = name.trim() !== (currentUser?.displayName || "");
    const dobChanged = dateOfBirth !== initialDateOfBirth;
    const waChanged = whatsappNumber !== initialWhatsappNumber;
    return !!newAvatarFile || nameChanged || dobChanged || waChanged;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return setProfileError("لم يتم تسجيل الدخول.");
    if (!name.trim()) return setProfileError("Name cannot be empty.");

    if (!hasProfileChanged()) {
      setProfileSuccess("No profile changes detected.");
      setTimeout(() => setProfileSuccess(""), 2000);
      return;
    }

    setProfileLoading(true);
    setProfileError("");
    setProfileSuccess("");

    let uploadPhotoURL = currentPhotoURL;

    try {
      if (newAvatarFile) {
        const avatarRef = ref(storage, `avatars/${currentUser.uid}/profile.jpg`);
        const uploadResult = await uploadBytes(avatarRef, newAvatarFile);
        uploadPhotoURL = await getDownloadURL(uploadResult.ref);
      }

      const profileUpdates = {};
      if (name.trim() !== (currentUser.displayName || "")) profileUpdates.displayName = name.trim();
      if (uploadPhotoURL !== (currentUser.photoURL || DEFAULT_AVATAR)) profileUpdates.photoURL = uploadPhotoURL;

      if (Object.keys(profileUpdates).length) await updateProfile(auth.currentUser, profileUpdates);

      const firestoreUpdates = {};
      if (name.trim() !== (currentUser.displayName || "")) firestoreUpdates.name = name.trim();
      if (uploadPhotoURL !== (currentUser.photoURL || DEFAULT_AVATAR)) firestoreUpdates.photoURL = uploadPhotoURL;
      if (dateOfBirth !== initialDateOfBirth) firestoreUpdates.dateOfBirth = dateOfBirth || null;
      if (whatsappNumber !== initialWhatsappNumber) firestoreUpdates.whatsappNumber = whatsappNumber.trim() || null;

      if (Object.keys(firestoreUpdates).length) {
        firestoreUpdates.updatedAt = serverTimestamp();
        await updateDoc(doc(db, "users", currentUser.uid), firestoreUpdates);
      }

      setCurrentPhotoURL(uploadPhotoURL);
      setNewAvatarFile(null);
      setInitialDateOfBirth(dateOfBirth);
      setInitialWhatsappNumber(whatsappNumber);

      setProfileSuccess("Profile updated successfully!");
      setTimeout(() => setProfileSuccess(""), 2500);
    } catch (error) {
      console.error(error);
      if (error.code === "auth/requires-recent-login")
        setProfileError("Please log out and log back in, then try again.");
      else setProfileError("Failed to update profile. Please try again.");
      if (newAvatarFile) setAvatarPreview(currentPhotoURL);
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChangeSubmit = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (!newPassword || !confirmPassword) return setPasswordError("Enter new password and confirmation.");
    if (newPassword !== confirmPassword) return setPasswordError("Passwords do not match.");
    if (newPassword.length < 6) return setPasswordError("Password must be at least 6 characters.");
    if (!auth.currentUser) return setPasswordError("Not authenticated.");

    setPasswordLoading(true);
    try {
      await updatePassword(auth.currentUser, newPassword);
      setPasswordSuccess("Password updated successfully!");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => setPasswordSuccess(""), 2500);
    } catch (error) {
      console.error(error);
      if (error.code === "auth/requires-recent-login")
        setPasswordError("This requires recent login. Log out and log back in.");
      else setPasswordError("Failed to update password.");
      setNewPassword("");
      setConfirmPassword("");
    } finally {
      setPasswordLoading(false);
    }
  };

  const fetchMyAttendanceSummary = useCallback(async () => {
    if (!currentUser) return;
    setAttLoading(true);
    setAttError("");

    try {
      const dates = listMonthDatesTZ(yyyy, mm);
      const startDate = dates[0];
      const endDate = dates[dates.length - 1];

      const qAtt = query(
        collection(db, "attendance"),
        where("userId", "==", currentUser.uid),
        where("date", ">=", startDate),
        where("date", "<=", endDate)
      );

      const snap = await getDocs(qAtt);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const byDate = new Map();
      rows.forEach((r) => { if (r.date) byDate.set(r.date, r); });

      // Load this employee's own schedule so working days / required hours match the manager view.
      let schedule = getUserSchedule(null);
      try {
        const uSnap = await getDoc(doc(db, "users", currentUser.uid));
        if (uSnap.exists()) schedule = getUserSchedule(uSnap.data());
      } catch (_) { /* fall back to default schedule */ }

      const userWorkingDates = dates.filter((ds) => schedule.workingDays.includes(weekdayIndexForDateStr(ds)));

      let totalWorkMs = 0;
      let vacationDays = 0;
      let paidVacationDays = 0;
      let unpaidVacationDays = 0;
      let presentDays = 0;
      let completeDays = 0;

      userWorkingDates.forEach((ds) => {
        const rec = byDate.get(ds);

        if (rec?.status === "Vacation") {
          vacationDays += 1;
          const vt = (rec?.vacation?.type || "").toLowerCase();
          if (vt.includes("unpaid")) unpaidVacationDays += 1;
          else paidVacationDays += 1;
          return;
        }

        // Cancelled / missed-checkout days are absences, not present.
        if (rec?.cancelled || rec?.absentReason === "no_checkout") return;

        const inDate = rec?.checkInTime?.toDate ? rec.checkInTime.toDate() : null;
        const outDate = rec?.checkOutTime?.toDate ? rec.checkOutTime.toDate() : null;

        if (!inDate) return;
        presentDays += 1;
        if (inDate && outDate) {
          const ms = outDate - inDate;
          if (ms > 0) {
            totalWorkMs += ms;
            completeDays += 1;
          }
        }
      });

      // Approved extra (compensatory) hours from anywhere in the month.
      let extraMs = 0;
      rows.forEach((r) => { extraMs += Number(r?.extraMs) || 0; });
      totalWorkMs += extraMs;

      const workingDaysCount = userWorkingDates.length;
      const absences = Math.max(0, workingDaysCount - presentDays - vacationDays);
      const requiredMs = workingDaysCount * schedule.requiredHoursPerDay * 3600 * 1000;
      const remainingMs = Math.max(0, requiredMs - totalWorkMs);

      setAttSummary({
        totalWorkMs,
        extraMs,
        requiredMs,
        remainingMs,
        absences,
        vacationDays,
        paidVacationDays,
        unpaidVacationDays,
        presentDays,
        completeDays,
        workingDays: workingDaysCount,
      });
    } catch (e) {
      console.error(e);
      setAttError("فشل تحميل ملخص الحضور.");
    } finally {
      setAttLoading(false);
    }
  }, [currentUser, yyyy, mm]);

  useEffect(() => {
    if (currentUser) fetchMyAttendanceSummary();
  }, [currentUser, fetchMyAttendanceSummary]);

  const fetchMyRequests = useCallback(async () => {
    if (!currentUser) return;
    setReqLoading(true);
    try {
      const qReq = query(
        collection(db, "workHoursRequests"),
        where("userId", "==", currentUser.uid),
        orderBy("createdAt", "desc")
      );
      const snap = await getDocs(qReq);
      setMyRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
    } finally {
      setReqLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) fetchMyRequests();
  }, [currentUser, fetchMyRequests]);

  /* =========================
     submit leave request to workHoursRequests
     ========================= */
  const submitLeaveRequest = async (e) => {
    e.preventDefault();
    setLrMsg("");

    if (!currentUser) return setLrMsg("لم يتم تسجيل الدخول.");
    if (!lrStart || !lrEnd) return setLrMsg("اختر تاريخ البداية والنهاية.");
    if (lrEnd < lrStart) return setLrMsg("تاريخ النهاية لا يمكن أن يكون قبل تاريخ البداية.");
    if (!lrReason.trim()) return setLrMsg("اكتب السبب.");

    setLrBusy(true);
    try {
      await addDoc(collection(db, "workHoursRequests"), {
        type: "leave",
        status: "Pending",

        userId: currentUser.uid,
        userName: currentUser.displayName || "",
        userEmail: currentUser.email || "",
        userRole: userRole || "",

        startDate: lrStart,
        endDate: lrEnd,
        leaveType: lrType,
        reason: lrReason.trim(),

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setLrMsg("✅ تم إرسال الطلب للمدير (قيد الانتظار).");
      setLrStart("");
      setLrEnd("");
      setLrType("Paid");
      setLrReason("");

      fetchMyRequests();
      setOpen((p) => ({ ...p, leave: false }));
    } catch (e2) {
      console.error(e2);
      setLrMsg("❌ فشل إرسال الطلب.");
    } finally {
      setLrBusy(false);
    }
  };

  /* =========================
     ✅ UPDATED: hours request must include details
     ========================= */
  const saveMyHours = async (e) => {
    e.preventDefault();
    setHoursMsg("");

    if (!currentUser) return setHoursMsg("لم يتم تسجيل الدخول.");
    if (!hoursDate) return setHoursMsg("اختر التاريخ.");
    if (!hoursIn && !hoursOut) return setHoursMsg("أدخل وقت الدخول أو الخروج.");

    // ✅ NEW: details required
    if (!hoursDetails.trim()) return setHoursMsg("يرجى كتابة تفاصيل هذا الطلب.");

    const timeOk = (t) => !t || /^\d{2}:\d{2}$/.test(t);
    if (!timeOk(hoursIn) || !timeOk(hoursOut)) return setHoursMsg("صيغة الوقت غير صحيحة.");

    if (hoursIn && hoursOut) {
      const [ih, im] = hoursIn.split(":").map(Number);
      const [oh, om] = hoursOut.split(":").map(Number);
      if (oh * 60 + om < ih * 60 + im) return setHoursMsg("وقت الخروج لا يمكن أن يكون قبل وقت الدخول.");
    }

    setHoursBusy(true);
    try {
      const [y, m, d] = hoursDate.split("-").map(Number);
      let inTs = null;
      let outTs = null;

      if (hoursIn) {
        const [ih, im] = hoursIn.split(":").map(Number);
        inTs = Timestamp.fromDate(makeUtcFromLocalInTZ(y, m, d, ih, im, TZ));
      }
      if (hoursOut) {
        const [oh, om] = hoursOut.split(":").map(Number);
        outTs = Timestamp.fromDate(makeUtcFromLocalInTZ(y, m, d, oh, om, TZ));
      }

      const attendanceId = makeAttendanceId(currentUser.uid, hoursDate);

      await addDoc(collection(db, "workHoursRequests"), {
        type: "hours",
        status: "Pending",

        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email || currentUser.uid,
        userEmail: currentUser.email || "",
        userRole: userRole || "",

        date: hoursDate,
        attendanceId,

        proposed: {
          checkInTime: inTs || null,
          checkOutTime: outTs || null,
        },

        // ✅ NEW
        details: hoursDetails.trim(),

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setHoursMsg("✅ تم إرسال الطلب للمدير (قيد الانتظار).");
      fetchMyRequests();
      setOpen((p) => ({ ...p, hours: false }));

      // clear inputs
      setHoursIn("");
      setHoursOut("");
      setHoursDetails(""); // ✅ NEW
    } catch (err) {
      console.error(err);
      setHoursMsg("❌ فشل إرسال الطلب.");
    } finally {
      setHoursBusy(false);
    }
  };

  if (authLoading) return <div className={styles.loading}>جارٍ التحميل…</div>;
  if (!currentUser) return <div className={styles.loading}>الرجاء تسجيل الدخول.</div>;

  // Requests UI helpers
  /* =========================
     submit extra (compensatory) hours request
     ========================= */
  const saveExtraHours = async (e) => {
    e.preventDefault();
    setXhMsg("");
    if (!currentUser) return setXhMsg("لم يتم تسجيل الدخول.");
    if (!xhDate) return setXhMsg("اختر التاريخ.");
    const hoursNum = Number(xhHours);
    if (!hoursNum || hoursNum <= 0) return setXhMsg("أدخل عدد الساعات التي عملتها.");
    if (hoursNum > 24) return setXhMsg("يجب أن تكون الساعات 24 أو أقل.");
    if (!xhDetails.trim()) return setXhMsg("يرجى وصف ما عملت عليه.");

    setXhBusy(true);
    try {
      const minutes = Math.round(hoursNum * 60);
      await addDoc(collection(db, "workHoursRequests"), {
        type: "extra_hours",
        status: "Pending",
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email || currentUser.uid,
        userEmail: currentUser.email || "",
        userRole: userRole || "",
        date: xhDate,
        attendanceId: makeAttendanceId(currentUser.uid, xhDate),
        minutes,
        workedFrom: xhFrom || "",
        details: xhDetails.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setXhMsg("✅ تم إرسال الطلب للمدير (قيد الانتظار).");
      fetchMyRequests();
      setXhHours("");
      setXhDetails("");
      setOpen((p) => ({ ...p, extra: false }));
    } catch (err) {
      console.error(err);
      setXhMsg("❌ فشل إرسال الطلب.");
    } finally {
      setXhBusy(false);
    }
  };

  const ROLE_AR = {
    Manager: "مدير",
    "Accounts Manager": "مدير حسابات",
    Sales: "مبيعات",
    "Content Creator": "صانع محتوى",
    HR: "موارد بشرية",
    Supervisor: "مشرف",
    Trainee: "متدرب",
    Employee: "موظف",
  };
  const roleAr = (r) => ROLE_AR[r] || r || "";
  const statusAr = (s) =>
    s === "Approved" ? "مقبول" : s === "Rejected" ? "مرفوض" : "قيد الانتظار";
  const typeAr = (t) =>
    t === "leave" ? "إجازة" : t === "hours" ? "ساعات" : t === "extra_hours" ? "ساعات إضافية" : "طلب";
  const vacTypeAr = (t) => (t === "Unpaid" ? "غير مدفوعة" : "مدفوعة");

  const requestLabel = (r) => {
    if (r.type === "leave") return `طلب إجازة • ${vacTypeAr(r.leaveType)}`;
    if (r.type === "hours") return `طلب ساعات • ${r.date || ""}`;
    if (r.type === "extra_hours") return `ساعات إضافية • ${r.date || ""}`;
    return "طلب";
  };

  const requestMeta = (r) => {
    if (r.type === "leave") return `${r.startDate || ""} ← ${r.endDate || ""} • ${r.reason || ""}`;
    if (r.type === "hours") {
      const details = r.details ? ` • ${r.details}` : "";
      return `المقترح: ${r?.proposed?.checkInTime ? "دخول ✓" : "دخول —"} / ${
        r?.proposed?.checkOutTime ? "خروج ✓" : "خروج —"
      }${details}`;
    }
    if (r.type === "extra_hours") {
      const h = ((Number(r.minutes) || 0) / 60).toFixed(2).replace(/\.00$/, "");
      const from = r.workedFrom ? ` • من ${r.workedFrom}` : "";
      return `+${h} س${from}${r.details ? ` • ${r.details}` : ""}`;
    }
    return "";
  };

  // A small "?" button that toggles a section's help box.
  const HelpToggle = ({ k }) => (
    <span
      className={styles.helpBtn}
      role="button"
      title="شرح"
      onClick={(e) => { e.stopPropagation(); toggleHelp(k); }}
    >
      ؟
    </span>
  );

  const HELP = {
    stats: (
      <>
        <strong>ملخّص دوامك للشهر المحدّد:</strong><br />
        • <strong>الساعات المُنجزة</strong>: مجموع ساعات عملك (شاملة الساعات الإضافية المعتمدة).<br />
        • <strong>الساعات المطلوبة</strong>: المطلوب منك هذا الشهر (أيام دوامك × الساعات المطلوبة يومياً، يحدّدها المدير).<br />
        • <strong>المتبقّي</strong>: كم ساعة باقية لتُكمل المطلوب (أخضر = أكملت، أحمر = ناقص).<br />
        • <strong>ساعات إضافية</strong>: الساعات الإضافية المعتمدة من المدير.<br />
        • <strong>أيام الحضور</strong>: الأيام التي سجّلت فيها دخولاً.<br />
        • <strong>الغيابات</strong>: أيام دوامك التي لم تحضر فيها.<br />
        • <strong>إجازات مدفوعة / غير مدفوعة</strong>: عدد أيام إجازاتك حسب النوع.<br />
        غيّر الشهر من الأعلى ثم اضغط «تحديث».
      </>
    ),
    requests: (
      <>
        قائمة بآخر طلباتك المُرسلة للمدير (إجازة، ساعات، أو ساعات إضافية) وحالة كل طلب:
        <strong> قيد الانتظار</strong> / <strong>مقبول</strong> / <strong>مرفوض</strong>. تُعرض آخر 8 طلبات.
      </>
    ),
    hours: (
      <>
        استخدم هذا القسم <strong>لتصحيح دوام يوم معيّن</strong> (مثلاً نسيت تسجيل الدخول أو الخروج).
        اختر التاريخ، وأدخل وقت الدخول و/أو الخروج، واكتب سبب التصحيح. يُرسَل الطلب للمدير، وعند القبول
        يُعدَّل دوام ذلك اليوم. الأوقات بتوقيت القدس.
      </>
    ),
    extra: (
      <>
        لطلب احتساب <strong>ساعات عملتها خارج الدوام الرسمي</strong> (مثلاً عوّضت تأخيرك بالعمل من البيت).
        اختر التاريخ، وأدخل عدد الساعات، ومكان العمل، ووصف ما عملت عليه بالضبط. عند موافقة المدير
        <strong> تُضاف هذه الساعات إلى إجمالي ساعاتك الشهرية</strong> (ولا تستبدل دوامك).
      </>
    ),
    profile: (
      <>
        لتحديث معلوماتك: الصورة الشخصية، الاسم، تاريخ الميلاد، ورقم واتساب (يُستخدم لإشعارات إسناد المهام).
        البريد الإلكتروني غير قابل للتعديل. اضغط «تحديث الملف» بعد التعديل.
      </>
    ),
    leave: (
      <>
        لتقديم <strong>طلب إجازة</strong>: اختر تاريخ البداية والنهاية، ونوع الإجازة (مدفوعة/غير مدفوعة)،
        واكتب السبب. يُرسَل الطلب للمدير للموافقة أو الرفض.
      </>
    ),
    password: (
      <>
        لتغيير <strong>كلمة مرور دخولك</strong>. أدخل كلمة المرور الجديدة وأكّدها ثم اضغط «تغيير كلمة المرور».
      </>
    ),
  };

  return (
    <div className={styles.page} dir="rtl">
      {/* ===== Top Header ===== */}
      <div className={styles.topCard}>
        <div className={styles.topLeft}>
          <img
            src={avatarPreview}
            alt="Avatar"
            className={styles.avatar}
            onError={(e) => (e.currentTarget.src = DEFAULT_AVATAR)}
          />
          <div className={styles.userText}>
            <div className={styles.userName}>{name || "مستخدم"}</div>
            <div className={styles.userMeta}>
              <span>{email}</span>
              <span className={styles.sep}>•</span>
              <span>{roleAr(roleDisplay)}</span>
            </div>
          </div>
        </div>

        <div className={styles.topRight}>
          <label className={styles.month}>
            <span>الشهر</span>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </label>
          <button className={styles.btnPrimary} onClick={fetchMyAttendanceSummary} disabled={attLoading}>
            {attLoading ? "…" : "تحديث"}
          </button>
        </div>
      </div>

      {/* ===== Stats ===== */}
      <div className={styles.listTitle} style={{ display: "flex", alignItems: "center", gap: 4, margin: "2px 2px 0" }}>
        <span>ملخّص الشهر <HelpToggle k="stats" /></span>
      </div>
      {helpOpen.stats && <div className={`${styles.helpBox} ${styles.helpBoxFlush}`}>{HELP.stats}</div>}
      <div className={styles.stats}>
        <div className={styles.stat}>
          <div className={styles.statLabel}>الساعات المُنجزة</div>
          <div className={styles.statValue}>{attLoading ? "…" : fmtHHMM(attSummary.totalWorkMs)}</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statLabel}>الساعات المطلوبة</div>
          <div className={styles.statValue}>
            {attLoading ? "…" : attSummary.requiredMs ? fmtHHMM(attSummary.requiredMs) : "—"}
          </div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statLabel}>المتبقّي</div>
          <div
            className={styles.statValue}
            style={!attLoading && attSummary.requiredMs ? { color: attSummary.remainingMs > 0 ? "#dc2626" : "#16a34a" } : undefined}
          >
            {attLoading ? "…" : attSummary.requiredMs ? fmtHHMM(attSummary.remainingMs) : "—"}
          </div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statLabel}>ساعات إضافية</div>
          <div className={styles.statValue}>{attLoading ? "…" : attSummary.extraMs ? `+${fmtHHMM(attSummary.extraMs)}` : "—"}</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statLabel}>أيام الحضور</div>
          <div className={styles.statValue}>{attLoading ? "…" : attSummary.presentDays}</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statLabel}>الغيابات</div>
          <div className={styles.statValue} style={!attLoading && attSummary.absences ? { color: "#dc2626" } : undefined}>
            {attLoading ? "…" : attSummary.absences}
          </div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statLabel}>إجازات مدفوعة</div>
          <div className={styles.statValue}>{attLoading ? "…" : attSummary.paidVacationDays}</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statLabel}>إجازات غير مدفوعة</div>
          <div className={styles.statValue}>{attLoading ? "…" : attSummary.unpaidVacationDays}</div>
        </div>
      </div>

      {attError && <div className={styles.alertError}>{attError}</div>}

      {/* ===== Requests Quick Panel ===== */}
      <div className={styles.listBox} style={{ marginTop: 14 }}>
        <div
          className={styles.listTitle}
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <span>طلباتي <HelpToggle k="requests" /></span>
          <button type="button" className={styles.btnGhost} onClick={fetchMyRequests} disabled={reqLoading}>
            {reqLoading ? "…" : "تحديث"}
          </button>
        </div>

        {helpOpen.requests && <div className={`${styles.helpBox} ${styles.helpBoxFlush}`}>{HELP.requests}</div>}

        {myRequests.length === 0 ? (
          <div className={styles.empty}>لا توجد طلبات بعد.</div>
        ) : (
          <div className={styles.list}>
            {myRequests.slice(0, 8).map((r) => (
              <div key={r.id} className={styles.item}>
                <div className={styles.itemTop}>
                  <div className={styles.itemDates}>{requestLabel(r)}</div>
                  <div
                    className={`${styles.status} ${
                      r.status === "Approved"
                        ? styles.statusOk
                        : r.status === "Rejected"
                        ? styles.statusBad
                        : styles.statusPending
                    }`}
                  >
                    {statusAr(r.status)}
                  </div>
                </div>
                <div className={styles.itemMeta}>
                  <span className={styles.badge}>{typeAr(r.type)}</span>
                  <span className={styles.reason}>{requestMeta(r)}</span>
                </div>
              </div>
            ))}
            {myRequests.length > 8 && <div className={styles.hint}>عرض آخر 8 طلبات.</div>}
          </div>
        )}
      </div>

      {/* ===== Sections (Accordion) ===== */}
      <div className={styles.sections}>
        {/* Add/Edit Hours */}
        <div className={styles.section}>
          <button className={styles.sectionBtn} onClick={() => toggle("hours")}>
            <div>
              <div className={styles.sectionTitle}>طلب إضافة / تعديل ساعات <HelpToggle k="hours" /></div>
              <div className={styles.sectionDesc}>إرسال طلب للمدير (قيد الانتظار ← مقبول/مرفوض).</div>
            </div>
            <div className={`${styles.chev} ${open.hours ? styles.chevOpen : ""}`}>▾</div>
          </button>

          {helpOpen.hours && <div className={styles.helpBox}>{HELP.hours}</div>}

          <div className={`${styles.sectionBody} ${open.hours ? styles.open : ""}`}>
            {hoursMsg && (
              <div className={hoursMsg.startsWith("✅") ? styles.alertOk : styles.alertError}>{hoursMsg}</div>
            )}

            <form onSubmit={saveMyHours} className={styles.form}>
              <div className={styles.grid}>
                <div className={styles.field}>
                  <label>التاريخ</label>
                  <input
                    type="date"
                    value={hoursDate}
                    onChange={(e) => setHoursDate(e.target.value)}
                    min={allMonthDates[0] || `${month}-01`}
                    max={allMonthDates[allMonthDates.length - 1] || `${month}-28`}
                    disabled={hoursBusy}
                  />
                </div>

                <div className={styles.field}>
                  <label>الدخول (بتوقيت القدس)</label>
                  <input type="time" value={hoursIn} onChange={(e) => setHoursIn(e.target.value)} disabled={hoursBusy} />
                </div>

                <div className={styles.field}>
                  <label>الخروج (بتوقيت القدس)</label>
                  <input type="time" value={hoursOut} onChange={(e) => setHoursOut(e.target.value)} disabled={hoursBusy} />
                </div>

                {/* ✅ NEW: Details required */}
                <div className={styles.field} style={{ gridColumn: "1 / -1" }}>
                  <label>التفاصيل (مطلوبة)</label>
                  <textarea
                    value={hoursDetails}
                    onChange={(e) => setHoursDetails(e.target.value)}
                    disabled={hoursBusy}
                    rows={3}
                    placeholder="وضّح ما يجب تصحيحه ولماذا (مثلاً نسيان الدخول، مشكلة بالنظام، عمل خارجي...)"
                  />
                </div>
              </div>

              <div className={styles.actions}>
                <button className={styles.btnPrimary} type="submit" disabled={hoursBusy}>
                  {hoursBusy ? "جارٍ الإرسال…" : "إرسال الطلب"}
                </button>

                <button
                  type="button"
                  className={styles.btnGhost}
                  onClick={() => {
                    setHoursIn("");
                    setHoursOut("");
                    setHoursDetails(""); // ✅ NEW
                    setHoursMsg("");
                  }}
                  disabled={hoursBusy}
                >
                  مسح
                </button>
              </div>

              <div className={styles.hint}>
                يتم اعتماد الأوقات بتوقيت <strong>{TZ}</strong> وتُحفظ بصيغة UTC.
              </div>
            </form>
          </div>
        </div>

        {/* Request Extra Hours */}
        <div className={styles.section}>
          <button className={styles.sectionBtn} onClick={() => toggle("extra")}>
            <div>
              <div className={styles.sectionTitle}>طلب ساعات إضافية <HelpToggle k="extra" /></div>
              <div className={styles.sectionDesc}>
                إرسال ساعات عملتها خارج تسجيل الدخول/الخروج للمدير (مثلاً تعويض تأخير من البيت).
              </div>
            </div>
            <div className={`${styles.chev} ${open.extra ? styles.chevOpen : ""}`}>▾</div>
          </button>

          {helpOpen.extra && <div className={styles.helpBox}>{HELP.extra}</div>}

          <div className={`${styles.sectionBody} ${open.extra ? styles.open : ""}`}>
            {xhMsg && (
              <div className={xhMsg.startsWith("✅") ? styles.alertOk : styles.alertError}>{xhMsg}</div>
            )}

            <form onSubmit={saveExtraHours} className={styles.form}>
              <div className={styles.grid}>
                <div className={styles.field}>
                  <label>التاريخ</label>
                  <input
                    type="date"
                    value={xhDate}
                    onChange={(e) => setXhDate(e.target.value)}
                    min={allMonthDates[0] || `${month}-01`}
                    max={allMonthDates[allMonthDates.length - 1] || `${month}-28`}
                    disabled={xhBusy}
                  />
                </div>

                <div className={styles.field}>
                  <label>عدد الساعات</label>
                  <input
                    type="number"
                    min="0"
                    max="24"
                    step="0.25"
                    value={xhHours}
                    onChange={(e) => setXhHours(e.target.value)}
                    placeholder="e.g. 2.5"
                    disabled={xhBusy}
                  />
                </div>

                <div className={styles.field}>
                  <label>مكان العمل</label>
                  <select value={xhFrom} onChange={(e) => setXhFrom(e.target.value)} disabled={xhBusy}>
                    <option value="Home">البيت</option>
                    <option value="Office">المكتب</option>
                    <option value="Client site">موقع العميل</option>
                    <option value="Other">أخرى</option>
                  </select>
                </div>

                <div className={styles.field} style={{ gridColumn: "1 / -1" }}>
                  <label>ماذا عملت؟ (مطلوب)</label>
                  <textarea
                    value={xhDetails}
                    onChange={(e) => setXhDetails(e.target.value)}
                    disabled={xhBusy}
                    rows={3}
                    placeholder="صف بالضبط ما عملت عليه خلال هذه الساعات…"
                  />
                </div>
              </div>

              <div className={styles.actions}>
                <button className={styles.btnPrimary} type="submit" disabled={xhBusy}>
                  {xhBusy ? "جارٍ الإرسال…" : "إرسال الطلب"}
                </button>
                <button
                  type="button"
                  className={styles.btnGhost}
                  onClick={() => { setXhHours(""); setXhDetails(""); setXhMsg(""); }}
                  disabled={xhBusy}
                >
                  مسح
                </button>
              </div>

              <div className={styles.hint}>تُضاف الساعات المعتمدة إلى إجمالي ساعاتك الشهرية.</div>
            </form>
          </div>
        </div>

        {/* Profile */}
        <div className={styles.section}>
          <button className={styles.sectionBtn} onClick={() => toggle("profile")}>
            <div>
              <div className={styles.sectionTitle}>ملفي الشخصي <HelpToggle k="profile" /></div>
              <div className={styles.sectionDesc}>تحديث الصورة والاسم وتاريخ الميلاد.</div>
            </div>
            <div className={`${styles.chev} ${open.profile ? styles.chevOpen : ""}`}>▾</div>
          </button>

          {helpOpen.profile && <div className={styles.helpBox}>{HELP.profile}</div>}

          <div className={`${styles.sectionBody} ${open.profile ? styles.open : ""}`}>
            {profileError && <div className={styles.alertError}>{profileError}</div>}
            {profileSuccess && <div className={styles.alertOk}>{profileSuccess}</div>}

            <form onSubmit={handleProfileSubmit} className={styles.form}>
              <div className={styles.avatarRow}>
                <img
                  src={avatarPreview}
                  alt="Preview"
                  className={styles.avatarLarge}
                  onError={(e) => (e.currentTarget.src = DEFAULT_AVATAR)}
                />
                <div className={styles.avatarActions}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    disabled={profileLoading}
                  />
                  <button
                    type="button"
                    className={styles.btnGhost}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={profileLoading}
                  >
                    تغيير الصورة
                  </button>
                  <div className={styles.hint}>الحد الأقصى 5 ميغابايت (JPG, PNG)</div>
                </div>
              </div>

              <div className={styles.grid}>
                <div className={styles.field}>
                  <label>البريد الإلكتروني</label>
                  <input value={email} disabled readOnly />
                </div>

                <div className={styles.field}>
                  <label>الاسم</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} disabled={profileLoading} />
                </div>

                <div className={styles.field}>
                  <label>تاريخ الميلاد</label>
                  <input
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    disabled={profileLoading}
                  />
                </div>

                <div className={styles.field}>
                  <label>رقم واتساب</label>
                  <input
                    type="tel"
                    value={whatsappNumber}
                    onChange={(e) => setWhatsappNumber(e.target.value)}
                    disabled={profileLoading}
                    placeholder="e.g. +970501234567"
                  />
                  <small style={{ opacity: 0.6 }}>الصيغة: +[رمز الدولة][الرقم] — تُستخدم لإشعارات إسناد المهام</small>
                </div>
              </div>

              <div className={styles.actions}>
                <button className={styles.btnPrimary} type="submit" disabled={profileLoading || !hasProfileChanged()}>
                  {profileLoading ? "جارٍ الحفظ…" : "تحديث الملف"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Leave Request */}
        <div className={styles.section}>
          <button className={styles.sectionBtn} onClick={() => toggle("leave")}>
            <div>
              <div className={styles.sectionTitle}>طلب إجازة <HelpToggle k="leave" /></div>
              <div className={styles.sectionDesc}>إرسال طلب للمدير (قيد الانتظار ← مقبول/مرفوض).</div>
            </div>
            <div className={`${styles.chev} ${open.leave ? styles.chevOpen : ""}`}>▾</div>
          </button>

          {helpOpen.leave && <div className={styles.helpBox}>{HELP.leave}</div>}

          <div className={`${styles.sectionBody} ${open.leave ? styles.open : ""}`}>
            {lrMsg && <div className={lrMsg.startsWith("✅") ? styles.alertOk : styles.alertError}>{lrMsg}</div>}

            <form onSubmit={submitLeaveRequest} className={styles.form}>
              <div className={styles.grid}>
                <div className={styles.field}>
                  <label>تاريخ البداية</label>
                  <input type="date" value={lrStart} onChange={(e) => setLrStart(e.target.value)} disabled={lrBusy} />
                </div>
                <div className={styles.field}>
                  <label>تاريخ النهاية</label>
                  <input type="date" value={lrEnd} onChange={(e) => setLrEnd(e.target.value)} disabled={lrBusy} />
                </div>
                <div className={styles.field}>
                  <label>النوع</label>
                  <select value={lrType} onChange={(e) => setLrType(e.target.value)} disabled={lrBusy}>
                    <option value="Paid">مدفوعة</option>
                    <option value="Unpaid">غير مدفوعة</option>
                  </select>
                </div>
                <div className={styles.field} style={{ gridColumn: "1 / -1" }}>
                  <label>السبب</label>
                  <textarea
                    value={lrReason}
                    onChange={(e) => setLrReason(e.target.value)}
                    disabled={lrBusy}
                    rows={3}
                    placeholder="اكتب التفاصيل…"
                  />
                </div>
              </div>

              <div className={styles.actions}>
                <button className={styles.btnPrimary} type="submit" disabled={lrBusy}>
                  {lrBusy ? "جارٍ الإرسال…" : "إرسال الطلب"}
                </button>
                <button type="button" className={styles.btnGhost} onClick={fetchMyRequests} disabled={reqLoading}>
                  {reqLoading ? "…" : "تحديث الطلبات"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Password */}
        <div className={styles.section}>
          <button className={styles.sectionBtn} onClick={() => toggle("password")}>
            <div>
              <div className={styles.sectionTitle}>تغيير كلمة المرور <HelpToggle k="password" /></div>
              <div className={styles.sectionDesc}>تحديث كلمة مرور الدخول.</div>
            </div>
            <div className={`${styles.chev} ${open.password ? styles.chevOpen : ""}`}>▾</div>
          </button>

          {helpOpen.password && <div className={styles.helpBox}>{HELP.password}</div>}

          <div className={`${styles.sectionBody} ${open.password ? styles.open : ""}`}>
            {passwordError && <div className={styles.alertError}>{passwordError}</div>}
            {passwordSuccess && <div className={styles.alertOk}>{passwordSuccess}</div>}

            <form onSubmit={handlePasswordChangeSubmit} className={styles.form}>
              <div className={styles.grid}>
                <div className={styles.field}>
                  <label>كلمة المرور الجديدة</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={passwordLoading}
                  />
                </div>
                <div className={styles.field}>
                  <label>تأكيد كلمة المرور</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={passwordLoading}
                  />
                </div>
              </div>

              <div className={styles.actions}>
                <button
                  className={styles.btnPrimary}
                  type="submit"
                  disabled={passwordLoading || !newPassword || !confirmPassword}
                >
                  {passwordLoading ? "جارٍ الحفظ…" : "تغيير كلمة المرور"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* footer hint */}
      <div className={styles.footerNote}>ملاحظة: على الهاتف، يفتح كل قسم كبطاقة مستقلة مرتبة.</div>
    </div>
  );
}
